export const MOTION_LABEL = "remoteControl.motion";
export const INPUT_LABEL = "remoteControl.input";
export const PROTOCOL_VERSION = 1;
export const MOTION_INTERVAL_MS = 16;
export const WHEEL_DELTA = 120;
export const WHEEL_ACCUM_THRESHOLD = 16;

const CODE_TO_VK = {
  KeyA: 0x41,
  KeyB: 0x42,
  KeyC: 0x43,
  KeyD: 0x44,
  KeyE: 0x45,
  KeyF: 0x46,
  KeyG: 0x47,
  KeyH: 0x48,
  KeyI: 0x49,
  KeyJ: 0x4a,
  KeyK: 0x4b,
  KeyL: 0x4c,
  KeyM: 0x4d,
  KeyN: 0x4e,
  KeyO: 0x4f,
  KeyP: 0x50,
  KeyQ: 0x51,
  KeyR: 0x52,
  KeyS: 0x53,
  KeyT: 0x54,
  KeyU: 0x55,
  KeyV: 0x56,
  KeyW: 0x57,
  KeyX: 0x58,
  KeyY: 0x59,
  KeyZ: 0x5a,
  Digit0: 0x30,
  Digit1: 0x31,
  Digit2: 0x32,
  Digit3: 0x33,
  Digit4: 0x34,
  Digit5: 0x35,
  Digit6: 0x36,
  Digit7: 0x37,
  Digit8: 0x38,
  Digit9: 0x39,
  Escape: 0x1b,
  Digit: 0x30,
  Backspace: 0x08,
  Tab: 0x09,
  Enter: 0x0d,
  NumpadEnter: 0x0d,
  ShiftLeft: 0x10,
  ShiftRight: 0x10,
  ControlLeft: 0xa2,
  ControlRight: 0xa3,
  AltLeft: 0xa4,
  AltRight: 0xa5,
  Space: 0x20,
  PageUp: 0x21,
  PageDown: 0x22,
  End: 0x23,
  Home: 0x24,
  ArrowLeft: 0x25,
  ArrowUp: 0x26,
  ArrowRight: 0x27,
  ArrowDown: 0x28,
  Insert: 0x2d,
  Delete: 0x2e,
  CapsLock: 0x14,
  F1: 0x70,
  F2: 0x71,
  F3: 0x72,
  F4: 0x73,
  F5: 0x74,
  F6: 0x75,
  F7: 0x76,
  F8: 0x77,
  F9: 0x78,
  F10: 0x79,
  F11: 0x7a,
  F12: 0x7b,
  Semicolon: 0xba,
  Equal: 0xbb,
  Comma: 0xbc,
  Minus: 0xbd,
  Period: 0xbe,
  Slash: 0xbf,
  Backquote: 0xc0,
  BracketLeft: 0xdb,
  Backslash: 0xdc,
  BracketRight: 0xdd,
  Quote: 0xde,
  Numpad0: 0x60,
  Numpad1: 0x61,
  Numpad2: 0x62,
  Numpad3: 0x63,
  Numpad4: 0x64,
  Numpad5: 0x65,
  Numpad6: 0x66,
  Numpad7: 0x67,
  Numpad8: 0x68,
  Numpad9: 0x69,
  NumpadMultiply: 0x6a,
  NumpadAdd: 0x6b,
  NumpadSubtract: 0x6d,
  NumpadDecimal: 0x6e,
  NumpadDivide: 0x6f,
};

const EXTENDED_CODES = {
  ArrowLeft: true,
  ArrowUp: true,
  ArrowRight: true,
  ArrowDown: true,
  Insert: true,
  Delete: true,
  Home: true,
  End: true,
  PageUp: true,
  PageDown: true,
  ControlRight: true,
  AltRight: true,
  NumpadEnter: true,
  NumpadDivide: true,
};

export function clamp01(value) {
  if (!Number.isFinite(value)) return 0;
  if (value < 0) return 0;
  if (value > 1) return 1;
  return value;
}

export function videoPointToNorm(videoEl, clientX, clientY) {
  if (!videoEl) return { x: 0, y: 0, inside: false };

  const rect = videoEl.getBoundingClientRect();
  const videoW = videoEl.videoWidth || rect.width;
  const videoH = videoEl.videoHeight || rect.height;
  const elW = rect.width;
  const elH = rect.height;

  if (elW <= 0 || elH <= 0 || videoW <= 0 || videoH <= 0) {
    return { x: 0, y: 0, inside: false };
  }

  const scale = Math.min(elW / videoW, elH / videoH);
  const contentW = videoW * scale;
  const contentH = videoH * scale;
  const padX = (elW - contentW) / 2;
  const padY = (elH - contentH) / 2;
  const x = (clientX - rect.left - padX) / contentW;
  const y = (clientY - rect.top - padY) / contentH;
  const inside = x >= 0 && x <= 1 && y >= 0 && y <= 1;

  return { x: clamp01(x), y: clamp01(y), inside };
}

export function buttonsFromEvent(event) {
  if (event.buttons != null) return event.buttons;
  if (event.button === 0) return 1;
  if (event.button === 1) return 4;
  if (event.button === 2) return 2;
  return 0;
}

export function buttonBitFromEvent(event) {
  if (event.button === 1) return 4;
  if (event.button === 2) return 2;
  return 1;
}

export function keyPayloadFromEvent(event) {
  return {
    code: event.code || "",
    key: event.key || "",
    location: event.location || 0,
    repeat: Boolean(event.repeat),
    vk: codeToVk(event.code, event.key),
    ext: Boolean(EXTENDED_CODES[event.code]),
  };
}

export function modsFromEvent(event) {
  return {
    c: Boolean(event.ctrlKey),
    a: Boolean(event.altKey),
    s: Boolean(event.shiftKey),
    m: Boolean(event.metaKey),
  };
}

function normalizeWheelDelta(value, deltaMode) {
  let delta = Number(value) || 0;

  if (deltaMode === 1) delta *= 16;
  else if (deltaMode === 2) delta *= 480;

  return delta;
}

export function wheelStepsFromEvent(event, accumRef) {
  if (!accumRef || typeof accumRef !== "object") {
    return { dx: 0, dy: 0 };
  }

  if (typeof accumRef.x !== "number") accumRef.x = 0;
  if (typeof accumRef.y !== "number") accumRef.y = 0;

  accumRef.x += normalizeWheelDelta(event.deltaX, event.deltaMode);
  accumRef.y += normalizeWheelDelta(event.deltaY, event.deltaMode);

  let dx = 0;
  let dy = 0;

  while (Math.abs(accumRef.x) >= WHEEL_ACCUM_THRESHOLD) {
    dx += accumRef.x > 0 ? WHEEL_DELTA : -WHEEL_DELTA;
    accumRef.x += accumRef.x > 0 ? -WHEEL_ACCUM_THRESHOLD : WHEEL_ACCUM_THRESHOLD;
  }

  while (Math.abs(accumRef.y) >= WHEEL_ACCUM_THRESHOLD) {
    dy += accumRef.y > 0 ? -WHEEL_DELTA : WHEEL_DELTA;
    accumRef.y += accumRef.y > 0 ? -WHEEL_ACCUM_THRESHOLD : WHEEL_ACCUM_THRESHOLD;
  }

  return { dx, dy };
}

export function isBlockedHotkey(event) {
  const code = event.code || (event.k && event.k.code) || "";
  const ctrl = Boolean(event.ctrlKey || (event.mods && event.mods.c));
  const alt = Boolean(event.altKey || (event.mods && event.mods.a));
  const shift = Boolean(event.shiftKey || (event.mods && event.mods.s));
  const meta = Boolean(event.metaKey || (event.mods && event.mods.m));

  if (code === "MetaLeft" || code === "MetaRight" || code === "OSLeft" || code === "OSRight") {
    return true;
  }

  if (ctrl && alt && (code === "Delete" || code === "End")) return true;
  if (meta && code === "KeyL") return true;
  if (ctrl && shift && code === "Escape") return true;

  return false;
}

export function codeToVk(code, key) {
  if (CODE_TO_VK[code] != null) return CODE_TO_VK[code];

  if (key && key.length === 1) {
    const char = key.toUpperCase().charCodeAt(0);

    if (char >= 32 && char <= 126) return char;
  }

  return 0;
}

/** Max clipboard text redirected controller → host (UTF-16 code units). */
export const REMOTE_CLIPBOARD_MAX_CHARS = 200000;

export function encodeRemoteControlEvent(partial) {
  return JSON.stringify({
    v: PROTOCOL_VERSION,
    sid: partial.sid,
    seq: partial.seq,
    ts: Date.now(),
    t: partial.t,
    x: partial.x,
    y: partial.y,
    b: partial.b || 0,
    dx: partial.dx || 0,
    dy: partial.dy || 0,
    k: partial.k || null,
    mods: partial.mods || null,
    op: partial.op || null,
    text: partial.text != null ? String(partial.text) : null,
  });
}

/** Ctrl/Cmd+V (plain paste) — clipboard must be redirected across machines. */
export function isPasteHotkey(event) {
  if (!event) return false;
  const code = event.code || (event.k && event.k.code) || "";
  const key = String(event.key || (event.k && event.k.key) || "").toLowerCase();
  const ctrl = Boolean(event.ctrlKey || (event.mods && event.mods.c));
  const meta = Boolean(event.metaKey || (event.mods && event.mods.m));
  const alt = Boolean(event.altKey || (event.mods && event.mods.a));
  const shift = Boolean(event.shiftKey || (event.mods && event.mods.s));

  if (alt || shift) return false;
  if (!(ctrl || meta)) return false;
  return code === "KeyV" || key === "v";
}

export function decodeRemoteControlEvent(raw) {
  let text = raw;

  if (raw == null) return null;

  if (typeof raw !== "string") {
    try {
      if (typeof Buffer !== "undefined" && Buffer.isBuffer && Buffer.isBuffer(raw)) {
        text = raw.toString("utf8");
      } else if (raw instanceof ArrayBuffer) {
        text = new TextDecoder().decode(raw);
      } else if (ArrayBuffer.isView(raw)) {
        text = new TextDecoder().decode(raw);
      } else {
        text = String(raw);
      }
    } catch (error) {
      return null;
    }
  }

  try {
    const event = JSON.parse(text);

    if (!event || event.v !== PROTOCOL_VERSION || !event.t || !event.sid) {
      return null;
    }

    return event;
  } catch (error) {
    return null;
  }
}

export function isRemoteControlLabel(label) {
  return label === MOTION_LABEL || label === INPUT_LABEL;
}
